import type { SortingState } from '@tanstack/react-table'
import { format, isValid } from 'date-fns'
import type {
  FileManagerTreeFolderNode,
  FileManagerTreeNode,
  FileManagerTreeNodeWithDepth,
  FileManagerTreeTableRow,
  FileManagerTreeUploadRow,
  LandingFile,
  UploadFilesFormValues,
} from './fileManagerTreeTypes'

const CONTENT_TYPE_LABELS: Record<string, string> = {
  'application/json': 'JSON file',
  'application/pdf': 'PDF document',
  'application/vnd.ms-excel': 'Excel spreadsheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel spreadsheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint presentation',
  'application/zip': 'ZIP archive',
  'image/gif': 'GIF image',
  'image/jpeg': 'JPEG image',
  'image/png': 'PNG image',
  'image/svg+xml': 'SVG image',
  'text/csv': 'CSV file',
  'text/html': 'HTML document',
  'text/markdown': 'Markdown document',
  'text/plain': 'Text file',
  'text/xml': 'XML document',
}

const CONTENT_TYPE_EMOJIS: Record<string, string> = {
  'application/json': '📝',
  'application/pdf': '📕',
  'application/vnd.ms-excel': '📊',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '📊',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '📝',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '📽️',
  'application/zip': '🗜️',
  'image/gif': '🖼️',
  'image/jpeg': '🖼️',
  'image/png': '🖼️',
  'image/svg+xml': '🖼️',
  'text/csv': '📊',
  'text/html': '🌐',
  'text/markdown': '📝',
  'text/plain': '📝',
  'text/xml': '📝',
}

const EXTENSION_EMOJIS: Record<string, string> = {
  csv: '📊',
  doc: '📝',
  docx: '📝',
  gif: '🖼️',
  html: '🌐',
  jpeg: '🖼️',
  jpg: '🖼️',
  json: '📝',
  md: '📝',
  pdf: '📕',
  png: '🖼️',
  ppt: '📽️',
  pptx: '📽️',
  svg: '🖼️',
  txt: '📝',
  xls: '📊',
  xlsx: '📊',
  xml: '📝',
  zip: '🗜️',
}

const DIRECTORY_PLACEHOLDER_FILE_NAME = '.arq_placeholder'

export function transformLandingFilesToTree(files: LandingFile[]): FileManagerTreeNode[] {
  const root: FileManagerTreeFolderNode = {
    nodeKind: 'folder',
    id: 'folder:root',
    name: '',
    path: '',
    children: [],
  }

  function getOrCreateFolder(parent: FileManagerTreeFolderNode, name: string) {
    const existingFolder = parent.children.find(
      (child): child is FileManagerTreeFolderNode => child.nodeKind === 'folder' && child.name === name
    )

    if (existingFolder) {
      return existingFolder
    }

    const path = parent.path ? `${parent.path}/${name}` : name
    const folder: FileManagerTreeFolderNode = {
      nodeKind: 'folder',
      id: `folder:${path}`,
      name,
      path,
      children: [],
    }

    parent.children.push(folder)
    return folder
  }

  files.forEach((file, index) => {
    const segments = (file.blobName ?? '').split('/').filter(Boolean)
    const folderSegments = segments.slice(0, -1)
    let parent = root

    for (const segment of folderSegments) {
      parent = getOrCreateFolder(parent, segment)
    }

    if (file.fileName === DIRECTORY_PLACEHOLDER_FILE_NAME || segments.at(-1) === DIRECTORY_PLACEHOLDER_FILE_NAME) {
      parent.placeholderFile = file
      return
    }

    const fileId = file.encryptedBlobName || file.blobName || file.fileName || `unknown-${index}`
    parent.children.push({ ...file, nodeKind: 'file', id: `file:${fileId}`, folderPath: parent.path })
  })

  sortTree(root, compareNodesByName)
  return root.children
}

export function getFiles(value: UploadFilesFormValues['files']) {
  if (Array.isArray(value)) {
    return value.filter((file): file is File => file instanceof File)
  }

  return value instanceof File ? [value] : []
}

function compareNodesByName(firstNode: FileManagerTreeNode, secondNode: FileManagerTreeNode) {
  if (firstNode.nodeKind !== secondNode.nodeKind) {
    return firstNode.nodeKind === 'folder' ? -1 : 1
  }

  const firstName = firstNode.nodeKind === 'folder' ? firstNode.name : firstNode.fileName || firstNode.blobName || ''
  const secondName =
    secondNode.nodeKind === 'folder' ? secondNode.name : secondNode.fileName || secondNode.blobName || ''

  return compareValues(firstName, secondName)
}

function sortTree(
  folder: FileManagerTreeFolderNode,
  compareNodes: (firstNode: FileManagerTreeNode, secondNode: FileManagerTreeNode) => number
) {
  folder.children.sort(compareNodes)

  for (const child of folder.children) {
    if (child.nodeKind === 'folder') {
      sortTree(child, compareNodes)
    }
  }
}

export function cloneTreeWithSorting(nodes: FileManagerTreeNode[], sorting: SortingState): FileManagerTreeNode[] {
  const [sort] = sorting
  const compareNodes = sort ? getSortingCompare(sort.id, sort.desc) : compareNodesByName
  const root: FileManagerTreeFolderNode = {
    nodeKind: 'folder',
    id: 'folder:root',
    name: '',
    path: '',
    children: nodes.map(cloneNode),
  }

  sortTree(root, compareNodes)
  return root.children
}

function cloneNode(node: FileManagerTreeNode): FileManagerTreeNode {
  if (node.nodeKind === 'file') {
    return { ...node }
  }

  return {
    ...node,
    children: node.children.map(cloneNode),
  }
}

export function filterTreeBySearch(nodes: FileManagerTreeNode[], searchValue: string): FileManagerTreeNode[] {
  const normalizedSearchValue = searchValue.trim().toLowerCase()

  if (!normalizedSearchValue) {
    return nodes
  }

  const filteredNodes: FileManagerTreeNode[] = []

  for (const node of nodes) {
    if (node.nodeKind === 'file') {
      if (nodeMatchesSearch(node, normalizedSearchValue)) {
        filteredNodes.push({ ...node })
      }
      continue
    }

    const filteredChildren = filterTreeBySearch(node.children, normalizedSearchValue)

    if (nodeMatchesSearch(node, normalizedSearchValue)) {
      filteredNodes.push({ ...node, children: node.children.map(cloneNode) })
      continue
    }

    if (filteredChildren.length > 0) {
      filteredNodes.push({ ...node, children: filteredChildren })
    }
  }

  return filteredNodes
}

function nodeMatchesSearch(node: FileManagerTreeNode, normalizedSearchValue: string) {
  const values =
    node.nodeKind === 'folder'
      ? [node.name, node.path]
      : [
          getDisplayName(node),
          node.blobName,
          node.contentType,
          formatContentType(node),
          node.createdBy,
          node.createdAt,
          formatCreatedAt(node.createdAt),
        ]

  return values.some((value) =>
    String(value ?? '')
      .toLowerCase()
      .includes(normalizedSearchValue)
  )
}

function getSortingCompare(columnId: string, desc: boolean) {
  return (firstNode: FileManagerTreeNode, secondNode: FileManagerTreeNode) => {
    if (firstNode.nodeKind !== secondNode.nodeKind) {
      return firstNode.nodeKind === 'folder' ? -1 : 1
    }

    const direction = desc ? -1 : 1
    const result = compareValues(getSortableValue(firstNode, columnId), getSortableValue(secondNode, columnId))
    return result * direction
  }
}

export function getSortableValue(node: FileManagerTreeNode, columnId: string) {
  if (node.nodeKind === 'folder') {
    return columnId === 'blobName' ? node.name : ''
  }

  if (columnId === 'blobName') return getDisplayName(node)
  if (columnId === 'contentType') return formatContentType(node)
  if (columnId === 'createdBy') return node.createdBy ?? ''
  if (columnId === 'createdAt') return node.createdAt ? new Date(node.createdAt).getTime() : 0

  return ''
}

function compareValues(firstValue: string | number, secondValue: string | number) {
  if (typeof firstValue === 'number' && typeof secondValue === 'number') {
    return firstValue - secondValue
  }

  return String(firstValue).localeCompare(String(secondValue), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

export function joinNamespace(...segments: string[]) {
  return segments
    .map((segment) => segment.trim().replace(/^\/+|\/+$/g, ''))
    .filter(Boolean)
    .join('/')
}

export function flattenTreeRows(
  nodes: FileManagerTreeNode[],
  expandedFolderIds: Set<string>,
  depth = 0
): FileManagerTreeTableRow[] {
  return nodes.flatMap((node) => {
    const row: FileManagerTreeNodeWithDepth = { ...node, depth }

    if (node.nodeKind === 'file' || !expandedFolderIds.has(node.id)) {
      return [row]
    }

    const uploadRow: FileManagerTreeUploadRow = {
      nodeKind: 'upload-action',
      id: `${node.id}:upload`,
      depth: depth + 1,
      folderPath: node.path,
      label: '...upload files',
    }

    if (node.children.length === 0) {
      return [row, uploadRow]
    }

    return [row, uploadRow, ...flattenTreeRows(node.children, expandedFolderIds, depth + 1)]
  })
}

export function formatCreatedAt(createdAt: LandingFile['createdAt']) {
  if (!createdAt) {
    return '-'
  }

  const date = new Date(createdAt)
  return isValid(date) ? format(date, 'PPp') : createdAt
}

export function formatContentType(file: LandingFile) {
  const contentType = file.contentType?.toLowerCase()

  if (contentType && CONTENT_TYPE_LABELS[contentType]) {
    return CONTENT_TYPE_LABELS[contentType]
  }

  const extension = file.fileName?.split('.').pop()

  if (extension && extension !== file.fileName) {
    return `${extension.toUpperCase()} file`
  }

  return file.contentType || 'Unknown file type'
}

function getFileExtension(file: LandingFile) {
  const extension = file.fileName?.split('.').pop()
  return extension && extension !== file.fileName ? extension.toLowerCase() : undefined
}

export function getFileEmoji(file: LandingFile) {
  const contentType = file.contentType?.toLowerCase()

  if (contentType && CONTENT_TYPE_EMOJIS[contentType]) {
    return CONTENT_TYPE_EMOJIS[contentType]
  }

  const extension = getFileExtension(file)

  if (extension && EXTENSION_EMOJIS[extension]) {
    return EXTENSION_EMOJIS[extension]
  }

  return '📄'
}

export function getDisplayName(node: FileManagerTreeNode) {
  if (node.nodeKind === 'folder') {
    return node.name
  }

  return node.fileName || node.blobName || 'Unnamed file'
}
